import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { PlanLimits } from "./PlanLimits";
import { PlanPermissions } from "./PlanPermissions";

type RegionalPrices = { priceMonthly?: string; priceYearly?: string; priceDaily?: string };

interface FormData {
  name: string;
  description: string;
  priceMonthly: string;
  priceDaily?: string;
  priceYearly?: string;
  annualDiscountPercent: number;
  currency: string;
  features: string;
  isActive: boolean;
  sortOrder: number;
  trialDurationDays: number;
  maxRecipes: number;
  maxIngredients: number;
  maxMaterials: number;
  unlimitedRecipes: boolean;
  unlimitedIngredients: boolean;
  unlimitedMaterials: boolean;
  permissions: any;
  paypalPlanId?: {
    daily?: string;
    monthly?: string;
    yearly?: string;
  };
  paypalSubscriptionUrl?: {
    daily?: string;
    monthly?: string;
    yearly?: string;
  };
  // Geo-based pricing: visitors from a country mapped to one of these
  // currencies (e.g. AU → AUD) see and are charged this price list instead of
  // the base prices above.
  pricesByCurrency?: Record<string, RegionalPrices>;
}

interface PlanFormProps {
  formData: FormData;
  onChange: (data: Partial<FormData>) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isLoading: boolean;
  isEditing: boolean;
}

export function PlanForm({ formData, onChange, onSubmit, onCancel, isLoading, isEditing }: PlanFormProps) {
  const [newRegionalCurrency, setNewRegionalCurrency] = useState("");

  const setRegionalPrice = (code: string, field: keyof RegionalPrices, value: string) => {
    onChange({
      pricesByCurrency: {
        ...(formData.pricesByCurrency || {}),
        [code]: { ...(formData.pricesByCurrency?.[code] || {}), [field]: value },
      },
    });
  };

  const addRegionalCurrency = () => {
    const code = newRegionalCurrency.trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(code) || formData.pricesByCurrency?.[code]) return;
    onChange({
      pricesByCurrency: { ...(formData.pricesByCurrency || {}), [code]: {} },
    });
    setNewRegionalCurrency("");
  };

  const removeRegionalCurrency = (code: string) => {
    const next = { ...(formData.pricesByCurrency || {}) };
    delete next[code];
    onChange({ pricesByCurrency: next });
  };

  const calculateYearlyPrice = (monthly: string, discount: number): string => {
    const monthlyNum = parseFloat(monthly) || 0;
    if (monthlyNum === 0) return "";
    const yearlyBeforeDiscount = monthlyNum * 12;
    const yearlyAfterDiscount = yearlyBeforeDiscount * (1 - discount / 100);
    return yearlyAfterDiscount.toFixed(2);
  };

  const calculatedYearlyPrice = calculateYearlyPrice(formData.priceMonthly, formData.annualDiscountPercent);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="name">Plan Name *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => onChange({ name: e.target.value })}
            placeholder="Starter"
          />
        </div>
        <div>
          <Label htmlFor="currency">Currency</Label>
          <Input
            id="currency"
            value={formData.currency}
            onChange={(e) => onChange({ currency: e.target.value })}
            placeholder="PHP"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder="Perfect for small businesses..."
          rows={2}
        />
      </div>

      <div className="space-y-4 border rounded-lg p-4 bg-muted/50">
        <h3 className="font-semibold">PayPal Integration (Optional)</h3>
        
        {formData.priceDaily && (
          <div className="space-y-2">
            <Label className="text-sm font-medium">Daily Billing</Label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="paypalPlanIdDaily" className="text-xs">Plan ID</Label>
                <Input
                  id="paypalPlanIdDaily"
                  value={formData.paypalPlanId?.daily || ''}
                  onChange={(e) => onChange({ 
                    paypalPlanId: { ...formData.paypalPlanId, daily: e.target.value }
                  })}
                  placeholder="P-XXXXX"
                  className="text-sm"
                />
              </div>
              <div>
                <Label htmlFor="paypalUrlDaily" className="text-xs">Subscription URL</Label>
                <Input
                  id="paypalUrlDaily"
                  value={formData.paypalSubscriptionUrl?.daily || ''}
                  onChange={(e) => onChange({ 
                    paypalSubscriptionUrl: { ...formData.paypalSubscriptionUrl, daily: e.target.value }
                  })}
                  placeholder="https://paypal.com/..."
                  className="text-sm"
                />
              </div>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label className="text-sm font-medium">Monthly Billing</Label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="paypalPlanIdMonthly" className="text-xs">Plan ID</Label>
              <Input
                id="paypalPlanIdMonthly"
                value={formData.paypalPlanId?.monthly || ''}
                onChange={(e) => onChange({ 
                  paypalPlanId: { ...formData.paypalPlanId, monthly: e.target.value }
                })}
                placeholder="P-YYYYY"
                className="text-sm"
              />
            </div>
            <div>
              <Label htmlFor="paypalUrlMonthly" className="text-xs">Subscription URL</Label>
              <Input
                id="paypalUrlMonthly"
                value={formData.paypalSubscriptionUrl?.monthly || ''}
                onChange={(e) => onChange({ 
                  paypalSubscriptionUrl: { ...formData.paypalSubscriptionUrl, monthly: e.target.value }
                })}
                placeholder="https://paypal.com/..."
                className="text-sm"
              />
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-sm font-medium">Yearly Billing</Label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="paypalPlanIdYearly" className="text-xs">Plan ID</Label>
              <Input
                id="paypalPlanIdYearly"
                value={formData.paypalPlanId?.yearly || ''}
                onChange={(e) => onChange({ 
                  paypalPlanId: { ...formData.paypalPlanId, yearly: e.target.value }
                })}
                placeholder="P-ZZZZZ"
                className="text-sm"
              />
            </div>
            <div>
              <Label htmlFor="paypalUrlYearly" className="text-xs">Subscription URL</Label>
              <Input
                id="paypalUrlYearly"
                value={formData.paypalSubscriptionUrl?.yearly || ''}
                onChange={(e) => onChange({ 
                  paypalSubscriptionUrl: { ...formData.paypalSubscriptionUrl, yearly: e.target.value }
                })}
                placeholder="https://paypal.com/..."
                className="text-sm"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <div>
          <Label htmlFor="priceDaily">Daily Price</Label>
          <Input
            id="priceDaily"
            type="number"
            step="0.01"
            value={formData.priceDaily || ''}
            onChange={(e) => onChange({ priceDaily: e.target.value })}
            placeholder="30.00"
          />
        </div>
        <div>
          <Label htmlFor="priceMonthly">Monthly Price</Label>
          <Input
            id="priceMonthly"
            type="number"
            step="0.01"
            value={formData.priceMonthly}
            onChange={(e) => onChange({ priceMonthly: e.target.value })}
            placeholder="299.00"
          />
        </div>
        <div>
          <Label htmlFor="discount">Annual Discount %</Label>
          <Input
            id="discount"
            type="number"
            min="0"
            max="100"
            value={formData.annualDiscountPercent || ""}
            onChange={(e) => onChange({ annualDiscountPercent: e.target.value === "" ? 0 : parseInt(e.target.value) })}
            placeholder="20"
          />
        </div>
        <div>
          <Label>Yearly Price</Label>
          <Input 
            type="number"
            value={formData.priceYearly || ''} 
            onChange={(e) => onChange({ priceYearly: e.target.value })}
            placeholder={calculatedYearlyPrice || "Enter yearly price"}
          />
          {calculatedYearlyPrice && !formData.priceYearly && (
            <p className="text-xs text-muted-foreground mt-1">
              Auto-calculated: {formData.currency} {calculatedYearlyPrice}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-3 border rounded-lg p-4 bg-muted/50">
        <div>
          <h3 className="font-semibold">Regional Pricing (Geo-Based)</h3>
          <p className="text-xs text-muted-foreground">
            Visitors from a country mapped to one of these currencies (e.g. Australia → AUD) see and
            are charged these prices instead of the base {formData.currency || "PHP"} prices.
            A currency with no prices set falls back to the base prices.
          </p>
        </div>

        {Object.entries(formData.pricesByCurrency || {}).map(([code, prices]) => (
          <div key={code} className="grid grid-cols-[3rem_1fr_1fr_1fr_auto] gap-2 items-end">
            <div className="font-medium text-sm pb-2">{code}</div>
            <div>
              <Label htmlFor={`regional-${code}-monthly`} className="text-xs">Monthly Price</Label>
              <Input
                id={`regional-${code}-monthly`}
                type="number"
                step="0.01"
                value={prices?.priceMonthly || ""}
                onChange={(e) => setRegionalPrice(code, "priceMonthly", e.target.value)}
                className="text-sm"
              />
            </div>
            <div>
              <Label htmlFor={`regional-${code}-yearly`} className="text-xs">Yearly Price</Label>
              <Input
                id={`regional-${code}-yearly`}
                type="number"
                step="0.01"
                value={prices?.priceYearly || ""}
                onChange={(e) => setRegionalPrice(code, "priceYearly", e.target.value)}
                className="text-sm"
              />
            </div>
            <div>
              <Label htmlFor={`regional-${code}-daily`} className="text-xs">Daily Price</Label>
              <Input
                id={`regional-${code}-daily`}
                type="number"
                step="0.01"
                value={prices?.priceDaily || ""}
                onChange={(e) => setRegionalPrice(code, "priceDaily", e.target.value)}
                className="text-sm"
              />
            </div>
            <Button variant="ghost" size="sm" onClick={() => removeRegionalCurrency(code)}>
              Remove
            </Button>
          </div>
        ))}

        <div className="flex gap-2 items-end">
          <div>
            <Label htmlFor="newRegionalCurrency" className="text-xs">Currency Code</Label>
            <Input
              id="newRegionalCurrency"
              value={newRegionalCurrency}
              onChange={(e) => setNewRegionalCurrency(e.target.value.toUpperCase())}
              placeholder="AUD"
              maxLength={3}
              className="w-24 text-sm"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={!/^[A-Z]{3}$/.test(newRegionalCurrency.trim())}
            onClick={addRegionalCurrency}
          >
            Add Currency
          </Button>
        </div>
      </div>

      <div>
        <Label htmlFor="features">Features (one per line)</Label>
        <Textarea
          id="features"
          value={formData.features}
          onChange={(e) => onChange({ features: e.target.value })}
          placeholder="Unlimited recipes&#10;5 team members&#10;Priority support"
          rows={6}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="sortOrder">Display Order</Label>
          <Input
            id="sortOrder"
            type="number"
            value={formData.sortOrder || ""}
            onChange={(e) => onChange({ sortOrder: e.target.value === "" ? 0 : parseInt(e.target.value) })}
            placeholder="0"
          />
        </div>
        <div>
          <Label htmlFor="trialDurationDays">Trial Duration (Days)</Label>
          <Input
            id="trialDurationDays"
            type="number"
            min="0"
            value={formData.trialDurationDays || ""}
            onChange={(e) => onChange({ trialDurationDays: e.target.value === "" ? 0 : parseInt(e.target.value) })}
            placeholder="14"
          />
          <p className="text-xs text-muted-foreground mt-1">Set to 0 for paid plans, 14+ for trial</p>
        </div>
      </div>

      <PlanLimits
        maxRecipes={formData.maxRecipes}
        maxIngredients={formData.maxIngredients}
        maxMaterials={formData.maxMaterials}
        unlimitedRecipes={formData.unlimitedRecipes}
        unlimitedIngredients={formData.unlimitedIngredients}
        unlimitedMaterials={formData.unlimitedMaterials}
        onChange={(field, value) => onChange({ [field]: value })}
      />

      <PlanPermissions
        permissions={formData.permissions}
        onChange={(permissions) => onChange({ permissions })}
      />

      <div className="flex items-center gap-2">
        <Switch
          checked={formData.isActive}
          onCheckedChange={(checked) => onChange({ isActive: checked })}
        />
        <Label>Active</Label>
      </div>

      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button 
          onClick={onSubmit}
          disabled={!formData.name || (!formData.priceMonthly && !formData.priceDaily) || isLoading}
        >
          {isLoading ? "Saving..." : "Save"}
        </Button>
      </div>
    </div>
  );
}
